/**
 * What a stored report's payment state says, and what it lets somebody do.
 *
 * Pure functions with tests, the treatment `route.ts`, `views.ts`, `alerts.ts`,
 * `countyDepth.ts` and `pricing.ts` get, and for the same reason: this is the
 * screen where somebody finds out whether the document they paid for is
 * theirs, so the wording *is* the decision.
 *
 * Three rules hold it together.
 *
 * **`has_pdf` and `paid` are different questions and both have to be asked.**
 * The first says a file was stored, the second says it was bought. A report
 * built for a checkout nobody completed has a perfectly good PDF sitting
 * behind a route that answers `report_unpaid`, so a download control drawn off
 * `has_pdf` alone is a button that fails every time it is pressed.
 *
 * **A deadline is stated, never implied.** A held report is swept when its
 * payment window closes, and a link that quietly stops working is worse than
 * one that said when it would. The moment comes from the server — the client
 * does no date arithmetic beyond comparing it to now, because a deadline
 * computed here is one that can disagree with the sweep.
 *
 * **Coming back from Stripe is not the same as being paid.** The redirect is
 * the buyer's browser; the release is a webhook, and the two race. So `done`
 * says the payment is landing and the list will show it shortly — it never
 * says "here is your document", which is a claim only the row can make.
 */

import type { ReportRecord } from "../api/types";
import { money } from "./pricing";

/** Day *and* time, because a window measured in hours needs both. */
export function deadlineWhen(value: string | null | undefined): string {
  if (!value) return "";
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

export interface ReportState {
  /** `held` owes money, `ready` has a file, `recorded` has neither. */
  kind: "held" | "expired" | "ready" | "recorded";
  /** What this row says about itself. Always something; never empty. */
  note: string;
  /** Whether the file can be fetched. False on everything but `ready`. */
  canDownload: boolean;
  /** Whether a payment link can still be minted for it. */
  canPay: boolean;
  /** The Pay control's label, with the amount in it. Empty unless `canPay`. */
  payLabel: string;
}

/**
 * What one archived report is, in the four states it can be in.
 *
 * `now` is a parameter rather than a call to the clock inside, which is
 * `scoring.today()`'s rule in reverse: the expiry branch has to be testable at
 * a chosen moment, and a module-level `Date.now()` would freeze at import.
 *
 * The `expired` branch exists because the row outlives its window by the
 * sweep's grace margin, so there is a real interval in which the report is
 * listed and cannot be bought. The server refuses that with `report_expired`;
 * offering the button anyway would be a dead end dressed as one, which is
 * `exportReadiness`' argument about a control refused after the click.
 */
export function reportState(
  report: ReportRecord,
  now: number = Date.now(),
): ReportState {
  if (!report.paid) {
    const due = money(report.due_cents);
    const closes = deadlineWhen(report.expires_at);
    const deadline = report.expires_at ? new Date(report.expires_at).getTime() : NaN;
    // Strictly past, so a deadline exactly now is still payable — the server
    // decides, and this must not refuse ahead of it.
    if (!Number.isNaN(deadline) && deadline <= now) {
      return {
        kind: "expired",
        note:
          `The payment window on this one has closed, so it can no longer be ` +
          `bought and is removed shortly. Generate it again for a current ` +
          `price — the counties have rewritten these registers since, so it ` +
          `will describe the county as it stands now.`,
        canDownload: false,
        canPay: false,
        payLabel: "",
      };
    }
    return {
      kind: "held",
      note: closes
        ? `Waiting on payment of ${due}. It is built and kept until ` +
          `${closes}, and the download opens the moment the payment lands.`
        : `Waiting on payment of ${due}. It is built and kept, and the ` +
          `download opens the moment the payment lands.`,
      canDownload: false,
      canPay: true,
      payLabel: `Pay ${due}`,
    };
  }

  if (report.has_pdf) {
    return { kind: "ready", note: "", canDownload: true, canPay: false, payLabel: "" };
  }
  // The record is real and the file is not. Said plainly rather than hidden —
  // a row with no download and no explanation reads as a broken page.
  return {
    kind: "recorded",
    note: "No document was stored for this one.",
    canDownload: false,
    canPay: false,
    payLabel: "",
  };
}

/**
 * What the return from Stripe says, before anything has been re-read.
 *
 * `done` deliberately promises nothing about the document. The webhook that
 * releases it is a separate delivery and may not have landed when the buyer's
 * browser does, so this says the payment is on its way and the list will show
 * it — the row itself is what eventually says the report is theirs.
 *
 * An unrecognised value is no message at all rather than a guess: this reads a
 * query parameter anybody can type.
 */
export function purchaseReturn(value: string | null | undefined): string {
  if (value === "done") {
    return (
      "Payment received. Releasing the document can take a moment — this " +
      "list updates as soon as it lands, and the report is kept either way."
    );
  }
  if (value === "cancelled") {
    return (
      "Checkout cancelled, and nothing was charged. The report is built and " +
      "waiting below — pay for it there, or delete it."
    );
  }
  return "";
}
